import { call, put, SagaGenerator, select } from "typed-redux-saga";

import { DataProvider } from "@com.mgmtp.a12.client/client-core/lib/core/data";
import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Relationship } from "@com.mgmtp.a12.dataservices/dataservices-access";
import { DataSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { FormEngineSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { DocumentServiceFactory, GroupInstance } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import {
	DataOperation,
	ProvideDataParams,
	ProvideDataResults,
	TreeEngineServerConnectorFactories
} from "@com.mgmtp.a12.treeengine/treeengine-core/lib/extensions/server-connector";
import { ApplicationActions } from "@com.mgmtp.a12.client/client-core/lib/core/application";

import { assertTrue } from "../../../utilities/assert";
import { entityInstancePathFromString } from "../../../utilities/pathHelpers";
import { convertAppIdToKey } from "../../../utilities/applicationKeyUtils";

import { ContactSelectionModalDescriptor } from "../descriptors";
import {
	EnumGroup,
	EnumValue,
	isContactData,
	isContactSelectionData,
	isEmpfaengerArray,
	isEmpfaengerEntry
} from "../types";
import {
	CONTACT_ENTRY_MODEL,
	CONTACT_LIST_NODE_MODEL,
	CONTACT_RELATIONSHIP_MODEL,
	DEFAULT_EMPFAENGER_TYP
} from "../const";
import { loadContactData, loadContactDataByClients } from "../utils";

const documentService = new DocumentServiceFactory().getDocumentService();

const treeEngineDataProvider = TreeEngineServerConnectorFactories.createDataProvider({
	dataLoader: {
		*provideData(params: ProvideDataParams): ProvideDataResults {
			const { activityId, queries } = params;
			yield* call(ensureDataLoaded, activityId);
			const queryResults = yield* call(handleQueries, activityId, queries);
			yield* put(ApplicationActions.setBusy(false));
			return { queryResults };
		}
	},
	preloadChildNodes: true,
	cancelChildActivity: false
});

export const contactSelectionModalProvider: DataProvider = {
	name: "ContactSelectionModalProvider",
	canHandle({ dataHolder, operation }) {
		return (
			ContactSelectionModalDescriptor.isInstance(dataHolder.descriptor) && ["save", "load"].includes(operation)
		);
	},
	*provideData(config) {
		yield* call(treeEngineDataProvider.provideData, config);
	}
};

function* ensureDataLoaded(activityId: string): SagaGenerator<void> {
	const existingData = yield* select(ActivitySelectors.data(activityId));
	if (isContactSelectionData(existingData)) {
		return;
	}

	try {
		const activity = yield* select(ActivitySelectors.activityById(activityId));
		assertTrue(ContactSelectionModalDescriptor.isInstance(activity?.descriptor));

		const { sourceAppKeys, targetGroupPath, entryMode, clients } = activity.descriptor;
		const appKeyList = parseAppKeys(sourceAppKeys);
		const clientsList = clients ? clients.split(",") : [];

		const alreadySetEmails = yield* call(
			readAlreadySetEmails,
			activity.initiatingActivityId,
			targetGroupPath,
			entryMode
		);

		if (appKeyList.length === 0) {
			yield* put(
				ActivityActions.setData({ activityId, data: { enumGroups: [], empfaengerTyp: DEFAULT_EMPFAENGER_TYP } })
			);
			return;
		}
		const rawEnumGroups = yield* call(resolveEnumGroups, activity.initiatingActivityId, appKeyList, clientsList);
		const enumGroups = filterAlreadySetContacts(rawEnumGroups, alreadySetEmails);
		yield* put(
			ActivityActions.setData({ activityId, data: { enumGroups, empfaengerTyp: DEFAULT_EMPFAENGER_TYP } })
		);
	} catch (error) {
		console.error("Error loading contact selection data", error);
		yield* put(
			ActivityActions.setData({ activityId, data: { enumGroups: [], empfaengerTyp: DEFAULT_EMPFAENGER_TYP } })
		);
	}
}

function* resolveEnumGroups(
	initiatingActivityId: string | undefined,
	sourceAppKeys: string[],
	clients: string[]
): SagaGenerator<EnumGroup[]> {
	const initiatingContactDataHolder = initiatingActivityId
		? yield* select(ActivitySelectors.dataHolderByDescriptor(initiatingActivityId, { type: "contactData" }))
		: undefined;
	const initiatingContactData = isContactData(initiatingContactDataHolder?.data)
		? initiatingContactDataHolder.data
		: undefined;
	const initiaitingActivity = initiatingActivityId
		? yield* select(ActivitySelectors.activityById(initiatingActivityId))
		: undefined;
	const docRef = initiaitingActivity?.descriptor.instance;

	const appKeysToFetch = initiatingContactData
		? sourceAppKeys.filter(appKey => {
				const key = convertAppIdToKey(appKey);
				return !Object.prototype.hasOwnProperty.call(initiatingContactData.values, key);
			})
		: sourceAppKeys;

	const cachedEnumGroups: EnumGroup[] = initiatingContactData
		? sourceAppKeys
				.filter(appKey => {
					const key = convertAppIdToKey(appKey);
					return Object.prototype.hasOwnProperty.call(initiatingContactData.values, key);
				})
				.map(appKey => {
					const key = convertAppIdToKey(appKey);
					return {
						key,
						name: initiatingContactData.names[key] ?? key,
						values: initiatingContactData.values[key] ?? [],
						resolved: true
					};
				})
		: [];

	// Use docRef-based fetch when available (form/process context);
	// fall back to clients-based fetch when opening from a context without a document (e.g. invitation modal).
	const fetchedEnumGroups: EnumGroup[] =
		appKeysToFetch.length > 0
			? docRef
				? yield* call(loadContactData, appKeysToFetch, docRef)
				: yield* call(loadContactDataByClients, appKeysToFetch, clients)
			: [];

	return [...cachedEnumGroups, ...fetchedEnumGroups];
}

function* readAlreadySetEmails(
	initiatingActivityId: string | undefined,
	targetGroupPath: string,
	entryMode: string | undefined
): SagaGenerator<Set<string>> {
	if (!initiatingActivityId) {
		return new Set();
	}

	const formEngineState = yield* select(FormEngineSelectors.engineState(initiatingActivityId));
	if (!formEngineState) {
		return new Set();
	}

	const document = DataSelectors.document()(formEngineState) as GroupInstance;
	const entityInstance = entityInstancePathFromString(targetGroupPath);
	const existingEntries = documentService.getAssignedObject(document, entityInstance);

	if (entryMode === "email") {
		const isEmailEntry = (v: unknown): v is { email: string } =>
			typeof v === "object" && v !== null && typeof (v as Record<string, unknown>).email === "string";
		const emailEntries = Array.isArray(existingEntries) ? existingEntries.filter(isEmailEntry) : [];
		return new Set(emailEntries.map(entry => entry.email));
	}

	const filteredEntries = Array.isArray(existingEntries) ? existingEntries.filter(it => isEmpfaengerEntry(it)) : [];
	if (!isEmpfaengerArray(filteredEntries)) {
		return new Set();
	}

	return new Set(filteredEntries.map(entry => entry.empfaenger));
}

function filterAlreadySetContacts(enumGroups: EnumGroup[], alreadySetEmails: Set<string>): EnumGroup[] {
	return enumGroups
		.map(group => ({
			...group,
			values: group.values.filter(value => !alreadySetEmails.has(value.value))
		}))
		.filter(group => group.values.length > 0);
}

function* handleQueries(
	activityId: string,
	queries: DataOperation.Query[]
): SagaGenerator<DataOperation.QueryResult[]> {
	const result: DataOperation.QueryResult[] = [];

	const data = yield* select(ActivitySelectors.data(activityId));
	const enumGroups: EnumGroup[] = isContactSelectionData(data) ? data.enumGroups : [];

	for (const query of queries) {
		if (DataOperation.Query.ListRootNodes.Query.isAssignableFrom(query)) {
			const entries = enumGroups.map(group => createRootLink(group));
			result.push({ id: query.id, entries });
		} else if (DataOperation.Query.ListChildNodes.Query.isAssignableFrom(query)) {
			const groupKey = extractGroupKeyFromSource(query.source);
			const group = enumGroups.find(g => g.key === groupKey);
			const entries = group ? group.values.map(value => createChildLink(group, value)) : [];
			result.push({ id: query.id, entries });
		} else {
			result.push({ id: query.id, entries: [] });
		}
	}

	return result;
}

/**
 * Creates a root-level link representing a contact list group (contactList_Node_D).
 * The document fields match contactList_Node_D: root.name, root.key
 */
function createRootLink(group: EnumGroup): Relationship.LinkWithDocument {
	const docRef = `${CONTACT_LIST_NODE_MODEL}/${group.key}`;
	return {
		document: {
			target: {
				root: {
					name: group.name ?? group.key,
					key: group.key
				}
			}
		},
		linkRef: {
			id: docRef,
			linkDescriptor: {
				relationshipModel: CONTACT_RELATIONSHIP_MODEL,
				entities: [
					{
						role: "list",
						modelName: CONTACT_LIST_NODE_MODEL,
						docRef
					}
				]
			}
		}
	};
}

/**
 * Creates a child link representing a contact entry (contactEntry_D).
 * The document fields match contactEntry_D: root.name, root.email
 * The relationship uses contactList_Content_R with roles list (parent) and entry (child).
 */
function createChildLink(group: EnumGroup, value: EnumValue): Relationship.LinkWithDocument {
	const parentDocRef = `${CONTACT_LIST_NODE_MODEL}/${group.key}`;
	const childDocRef = `${CONTACT_ENTRY_MODEL}/${group.key}_${encodeURIComponent(value.value)}`;
	const linkId = `${group.key}_${encodeURIComponent(value.value)}`;

	return {
		document: {
			target: {
				root: {
					name: value.label,
					email: value.value
				}
			}
		},
		linkRef: {
			id: linkId,
			linkDescriptor: {
				relationshipModel: CONTACT_RELATIONSHIP_MODEL,
				position: Relationship.LinkPosition.TOP,
				entities: [
					{
						role: "entry",
						modelName: CONTACT_ENTRY_MODEL,
						docRef: childDocRef
					},
					{
						role: "list",
						modelName: CONTACT_LIST_NODE_MODEL,
						docRef: parentDocRef
					}
				]
			}
		}
	};
}

function extractGroupKeyFromSource(source: string): string {
	const parts = source.split("/");
	return parts.length > 1 ? parts[1] : source;
}

function parseAppKeys(serializedAppKeyss: string): string[] {
	return serializedAppKeyss
		.split(",")
		.map(s => s.trim())
		.filter(s => s.length > 0);
}
